import { useContext, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GlobalStateContext } from "../../layouts/GlobalStates";
import { ToastContext } from "../../component/global/ToastContext";
import { get, patch, post } from "../../controlers/global/ConfigRequest";
import {
  ADMIN_COMPANY_LIST,
  ADMIN_USER_ACCESS,
  ADMIN_USER_CREATE,
  ADMIN_USER_ENTER,
  ADMIN_USER_LIST,
  ADMIN_USER_SET_ACTIVE,
} from "../../helperData/ApiAddress";
import { persistAppFlavor, getPostLoginFlavor } from "../../helperData/appFlavor";
import { stashSuperuserSession } from "../../helperData/orgImpersonation";
import {
  buildAuthState,
  getPostLoginPath,
  persistAuthSession,
} from "../../helperData/panelRoutes";

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) return data.detail.join(" ");
  const fieldErrors = Object.values(data).flat().filter(Boolean);
  return fieldErrors.length ? String(fieldErrors.join(" ")) : null;
};

const toLocalMobile = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("0098")) digits = digits.slice(4);
  if (digits.startsWith("98") && digits.length >= 12) {
    digits = `0${digits.slice(2, 12)}`;
  } else if (digits.startsWith("9") && digits.length === 10) {
    digits = `0${digits}`;
  } else if (digits.startsWith("0")) {
    digits = digits.slice(0, 11);
  }
  return /^09\d{9}$/.test(digits) ? digits : "";
};

const defaultUsername = (user) => {
  const fromUsername = toLocalMobile(user?.username);
  if (fromUsername) return fromUsername;
  if (!user?.username) return toLocalMobile(user?.phone_number);
  return user.username;
};

const emptyCreateForm = () => ({
  first_name: "",
  last_name: "",
  phone_number: "",
  email: "",
  username: "",
  password: "",
  passwordConfirm: "",
  showPassword: false,
  company_id: "",
  company_admin: false,
  is_active: true,
});

const emptyAccessForm = (user) => ({
  username: defaultUsername(user),
  password: "",
  passwordConfirm: "",
  showPassword: false,
  company_id: user?.company ? String(user.company) : "",
  company_admin: Boolean(user?.company_admin),
});

export default function UsersTab() {
  const { auth, setAuth, setToggleSidebar } = useContext(GlobalStateContext);
  const show = useContext(ToastContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === "fa" ? "fa-IR" : "en-US";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [passwordFilter, setPasswordFilter] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAccessForm());
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    get(ADMIN_COMPANY_LIST)
      .then((data) => setOrgs(Array.isArray(data) ? data : data?.results || []))
      .catch(() => show(t("OrgManagement.Error"), "danger"));
    // show/t are stable enough for this one-shot load and must not retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, page_size: 25 };
    if (search) params.search = search;
    if (companyFilter) params.company = companyFilter;
    if (roleFilter) params.role = roleFilter;
    if (statusFilter) params.is_active = statusFilter;
    if (passwordFilter) params.credentials = passwordFilter;

    get(ADMIN_USER_LIST, params)
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data?.results || [];
        setLoadError("");
        setUsers(rows);
        setCount(data?.count ?? rows.length);
        setLastPage(data?.lastPage || 1);
      })
      .catch((error) => {
        if (!cancelled) {
          const message = getApiErrorMessage(error) || t("OrgManagement.Error");
          setLoadError(message);
          setUsers([]);
          show(message, "danger");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, companyFilter, roleFilter, statusFilter, passwordFilter, page, reloadKey]);

  const assignableOrgs = orgs.filter((org) => !org.is_deleted);
  const isProtected = (user) => user.role === "system" || user.is_superuser;

  const formatWhen = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const enterBlockReason = (user) => {
    if (isProtected(user)) return t("OrgManagement.Users.Protected");
    if (!user.is_active) return t("OrgManagement.Users.Enter.DisabledInactive");
    if (user.company_is_deleted || user.company_is_suspended) {
      return t("OrgManagement.Users.Enter.DisabledOrg");
    }
    return "";
  };

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    setCreateError("");
    setShowCreate(true);
  };

  const saveCreate = () => {
    const firstName = createForm.first_name.trim();
    const lastName = createForm.last_name.trim();
    const phone = createForm.phone_number.trim();
    const username = createForm.username.trim();
    if (!firstName || !lastName) {
      setCreateError(t("OrgManagement.Users.Create.Name.Required"));
      return;
    }
    if (!phone) {
      setCreateError(t("OrgManagement.Users.Create.Phone.Required"));
      return;
    }
    if (username.length < 3) {
      setCreateError(t("OrgManagement.Users.Access.Username.Required"));
      return;
    }
    if (createForm.password.length < 8) {
      setCreateError(t("OrgManagement.Users.Access.Password.Short"));
      return;
    }
    if (createForm.password !== createForm.passwordConfirm) {
      setCreateError(t("OrgManagement.Users.Access.PasswordConfirm.Mismatch"));
      return;
    }

    setCreating(true);
    post(ADMIN_USER_CREATE, {
      first_name: firstName,
      last_name: lastName,
      phone_number: phone,
      email: createForm.email.trim(),
      username,
      password: createForm.password,
      company_id: createForm.company_id ? Number(createForm.company_id) : null,
      company_admin: Boolean(createForm.company_id) && createForm.company_admin,
      is_active: createForm.is_active,
    })
      .then(() => {
        setShowCreate(false);
        setPage(1);
        setReloadKey((current) => current + 1);
        show(t("OrgManagement.Users.Create.Success"), "success");
      })
      .catch((error) =>
        setCreateError(getApiErrorMessage(error) || t("OrgManagement.Error"))
      )
      .finally(() => setCreating(false));
  };

  const openAccess = (user) => {
    setEditing(user);
    setForm(emptyAccessForm(user));
    setFormError("");
  };

  const saveAccess = () => {
    const username = form.username.trim();
    const usernameChanged = username !== (editing.username || "");
    if (username.length < 3) {
      setFormError(t("OrgManagement.Users.Access.Username.Required"));
      return;
    }
    if (usernameChanged && !editing.has_password && !form.password) {
      setFormError(t("OrgManagement.Users.Access.Password.Required"));
      return;
    }
    if (form.password && form.password.length < 8) {
      setFormError(t("OrgManagement.Users.Access.Password.Short"));
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setFormError(t("OrgManagement.Users.Access.PasswordConfirm.Mismatch"));
      return;
    }

    const payload = {
      company_id: form.company_id ? Number(form.company_id) : null,
      company_admin: Boolean(form.company_id) && form.company_admin,
    };
    if (usernameChanged) payload.username = username;
    if (form.password) payload.password = form.password;

    setSaving(true);
    patch(ADMIN_USER_ACCESS.replace("{id}", editing.id), payload)
      .then(() => {
        setEditing(null);
        setPage(1);
        setReloadKey((current) => current + 1);
        show(t("OrgManagement.Users.Access.Success"), "success");
      })
      .catch((error) =>
        setFormError(getApiErrorMessage(error) || t("OrgManagement.Error"))
      )
      .finally(() => setSaving(false));
  };

  const toggleActive = (user) => {
    const next = !user.is_active;
    if (!next) {
      const message = user.company_admin
        ? t("OrgManagement.Users.Deactivate.AdminConfirm")
        : t("OrgManagement.Users.Deactivate.Confirm");
      if (!window.confirm(message)) return;
    }
    setBusyId(user.id);
    post(ADMIN_USER_SET_ACTIVE.replace("{id}", user.id), { is_active: next })
      .then((updated) => {
        setUsers((prev) => {
          const hiddenByFilter =
            (statusFilter === "true" && !updated.is_active) ||
            (statusFilter === "false" && updated.is_active);
          if (hiddenByFilter) {
            return prev.filter((item) => item.id !== updated.id);
          }
          return prev.map((item) => (item.id === updated.id ? updated : item));
        });
        setCount((current) => {
          const hiddenByFilter =
            (statusFilter === "true" && !updated.is_active) ||
            (statusFilter === "false" && updated.is_active);
          return hiddenByFilter ? Math.max(0, current - 1) : current;
        });
        show(t("OrgManagement.Users.Status.Success"), "success");
      })
      .catch((error) =>
        show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger")
      )
      .finally(() => setBusyId(null));
  };

  const enterAsUser = (user) => {
    setBusyId(user.id);
    post(ADMIN_USER_ENTER.replace("{id}", user.id))
      .then((response) => {
        const name = user.full_name || user.username;
        stashSuperuserSession(
          { id: auth.id, username: auth.username },
          name
        );
        persistAuthSession(response);
        const flavor = getPostLoginFlavor(response.user);
        persistAppFlavor(flavor);
        setAuth(buildAuthState(auth, response));
        document.body.classList.remove("no_overflow");
        setToggleSidebar(false);
        show(t("OrgManagement.Users.Enter.Success", { name }), "success");
        navigate(getPostLoginPath(response.user, flavor));
      })
      .catch((error) =>
        show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger")
      )
      .finally(() => setBusyId(null));
  };

  const changeFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="organizations_toolbar">
        <div>
          <div className="organizations_title">{t("OrgManagement.Users.Title")}</div>
          <div className="user_secondary">
            {t("OrgManagement.Users.Lead")} · {t("OrgManagement.Users.Count", { count })}
          </div>
        </div>
        <div className="organizations_toolbar_actions">
          <Button variant="primary" onClick={openCreate}>
            + {t("OrgManagement.Users.New")}
          </Button>
          <input
            className="organizations_search"
            type="search"
            value={searchInput}
            placeholder={t("OrgManagement.Users.Search")}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <select
            className="organizations_filter"
            value={companyFilter}
            aria-label={t("OrgManagement.Users.Column.Organization")}
            onChange={changeFilter(setCompanyFilter)}
          >
            <option value="">{t("OrgManagement.Users.Filter.AllOrgs")}</option>
            <option value="none">{t("OrgManagement.Users.Filter.NoOrg")}</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <select
            className="organizations_filter"
            value={roleFilter}
            aria-label={t("OrgManagement.Users.Column.Role")}
            onChange={changeFilter(setRoleFilter)}
          >
            <option value="">{t("OrgManagement.Users.Filter.AllRoles")}</option>
            <option value="admin">{t("OrgManagement.Users.Role.admin")}</option>
            <option value="member">{t("OrgManagement.Users.Role.member")}</option>
            <option value="customer">{t("OrgManagement.Users.Role.customer")}</option>
            <option value="system">{t("OrgManagement.Users.Role.system")}</option>
          </select>
          <select
            className="organizations_filter"
            value={statusFilter}
            aria-label={t("OrgManagement.Users.Column.Status")}
            onChange={changeFilter(setStatusFilter)}
          >
            <option value="">{t("OrgManagement.Users.Filter.AllStatuses")}</option>
            <option value="true">{t("OrgManagement.Users.Filter.Active")}</option>
            <option value="false">{t("OrgManagement.Users.Filter.Inactive")}</option>
          </select>
          <select
            className="organizations_filter"
            value={passwordFilter}
            aria-label={t("OrgManagement.Users.Column.Password")}
            onChange={changeFilter(setPasswordFilter)}
          >
            <option value="">{t("OrgManagement.Users.Filter.AllPasswords")}</option>
            <option value="set">{t("OrgManagement.Users.Filter.PasswordSet")}</option>
            <option value="missing">{t("OrgManagement.Users.Filter.PasswordMissing")}</option>
          </select>
        </div>
      </div>

      <div className="organizations_table_wrap">
        {loading ? (
          <div className="organizations_loading">
            <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
          </div>
        ) : loadError ? (
          <div className="organizations_empty">{loadError}</div>
        ) : users.length === 0 ? (
          <div className="organizations_empty">{t("OrgManagement.Users.Empty")}</div>
        ) : (
          <Table responsive className="organizations_table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t("OrgManagement.Users.Column.Name")}</th>
                <th>{t("OrgManagement.Users.Column.Username")}</th>
                <th>{t("OrgManagement.Users.Column.Phone")}</th>
                <th>{t("OrgManagement.Users.Column.Organization")}</th>
                <th>{t("OrgManagement.Users.Column.Role")}</th>
                <th>{t("OrgManagement.Users.Column.Password")}</th>
                <th>{t("OrgManagement.Users.Column.LastLogin")}</th>
                <th>{t("OrgManagement.Users.Column.Status")}</th>
                <th>{t("OrgManagement.Enter.Button")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const blocked = enterBlockReason(user);
                const protectedUser = isProtected(user);
                return (
                  <tr key={user.id} className={user.is_active ? "" : "is_deleted"}>
                    <td>{(page - 1) * 25 + index + 1}</td>
                    <td>
                      <div className="user_primary">{user.full_name}</div>
                      {user.email ? (
                        <div className="user_secondary">{user.email}</div>
                      ) : null}
                      {user.user_group_name ? (
                        <div className="user_secondary">
                          {t("OrgManagement.Users.Group", {
                            name: user.user_group_name,
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td>{toLocalMobile(user.username) || user.username || "—"}</td>
                    <td>{user.phone_number || "—"}</td>
                    <td>{user.company_name || t("OrgManagement.Users.NoOrganization")}</td>
                    <td>
                      <span className={`org_status ${user.role}`}>
                        {t(`OrgManagement.Users.Role.${user.role}`)}
                      </span>
                    </td>
                    <td>
                      <span className={`org_status ${user.has_password ? "active" : "no_admin"}`}>
                        {user.has_password
                          ? t("OrgManagement.Users.Password.Set")
                          : t("OrgManagement.Users.Password.Missing")}
                      </span>
                    </td>
                    <td>{formatWhen(user.last_login)}</td>
                    <td>
                      <span className={`org_status ${user.is_active ? "active" : "inactive"}`}>
                        {user.is_active
                          ? t("OrgManagement.Users.Status.Active")
                          : t("OrgManagement.Users.Status.Inactive")}
                      </span>
                    </td>
                    <td className="org_actions_cell">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={Boolean(blocked) || busyId === user.id}
                        title={blocked || undefined}
                        onClick={() => enterAsUser(user)}
                      >
                        {busyId === user.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("OrgManagement.Enter.Button")
                        )}
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="info"
                        disabled={protectedUser || busyId === user.id}
                        title={protectedUser ? t("OrgManagement.Users.Protected") : undefined}
                        onClick={() => openAccess(user)}
                      >
                        {t("OrgManagement.Users.Access.Button")}
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant={user.is_active ? "warning" : "success"}
                        disabled={protectedUser || busyId === user.id}
                        title={protectedUser ? t("OrgManagement.Users.Protected") : undefined}
                        onClick={() => toggleActive(user)}
                      >
                        {user.is_active
                          ? t("OrgManagement.Users.Deactivate")
                          : t("OrgManagement.Users.Activate")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      {lastPage > 1 ? (
        <div className="users_pagination">
          <span>{t("OrgManagement.Users.Page", { current: page, last: lastPage })}</span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              {t("OrgManagement.Users.Prev")}
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              {t("OrgManagement.Users.Next")}
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        show={Boolean(editing)}
        onHide={() => setEditing(null)}
        centered
        className="org_admin_modal"
      >
        <Modal.Header>
          <Modal.Title>
            {t("OrgManagement.Users.Access.Title", {
              name: editing?.full_name || "",
            })}
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setEditing(null)}
          />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Username")}</Form.Label>
            <Form.Control
              value={form.username}
              autoComplete="off"
              onChange={(event) =>
                setForm((current) => ({ ...current, username: event.target.value }))
              }
            />
            <Form.Text>{t("OrgManagement.Users.Access.Username.Help")}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Password")}</Form.Label>
            <Form.Control
              type={form.showPassword ? "text" : "password"}
              value={form.password}
              autoComplete="new-password"
              onChange={(event) =>
                setForm((current) => ({ ...current, password: event.target.value }))
              }
            />
            <Form.Text>{t("OrgManagement.Users.Access.Password.Help")}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.PasswordConfirm")}</Form.Label>
            <Form.Control
              type={form.showPassword ? "text" : "password"}
              value={form.passwordConfirm}
              autoComplete="new-password"
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  passwordConfirm: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Check
            className="mb-3"
            type="checkbox"
            label={t("OrgManagement.Users.Access.ShowPassword")}
            checked={form.showPassword}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                showPassword: event.target.checked,
              }))
            }
          />
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Organization")}</Form.Label>
            <Form.Select
              value={form.company_id}
              onChange={(event) => {
                const company_id = event.target.value;
                setForm((current) => ({
                  ...current,
                  company_id,
                  company_admin: company_id ? current.company_admin : false,
                }));
              }}
            >
              <option value="">{t("OrgManagement.Users.NoOrganization")}</option>
              {assignableOrgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="checkbox"
            label={t("OrgManagement.Users.Access.CompanyAdmin")}
            checked={form.company_admin}
            disabled={!form.company_id}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                company_admin: event.target.checked,
              }))
            }
          />
          {formError ? <div className="text-danger mt-3">{formError}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditing(null)}>
            {t("OrgManagement.Create.Cancel")}
          </Button>
          <Button variant="primary" disabled={saving} onClick={saveAccess}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              t("OrgManagement.Users.Access.Submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        centered
        className="org_admin_modal"
      >
        <Modal.Header>
          <Modal.Title>{t("OrgManagement.Users.Create.Title")}</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShowCreate(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Create.FirstName")}</Form.Label>
            <Form.Control
              value={createForm.first_name}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  first_name: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Create.LastName")}</Form.Label>
            <Form.Control
              value={createForm.last_name}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  last_name: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Column.Phone")}</Form.Label>
            <Form.Control
              value={createForm.phone_number}
              inputMode="tel"
              placeholder="09123456789"
              onChange={(event) => {
                const phone_number = event.target.value;
                setCreateForm((current) => {
                  const previousAuto = toLocalMobile(current.phone_number);
                  const nextAuto = toLocalMobile(phone_number);
                  const username =
                    !current.username || current.username === previousAuto
                      ? nextAuto || current.username
                      : current.username;
                  return { ...current, phone_number, username };
                });
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Create.Email")}</Form.Label>
            <Form.Control
              type="email"
              value={createForm.email}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Username")}</Form.Label>
            <Form.Control
              value={createForm.username}
              autoComplete="off"
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  username: event.target.value,
                }))
              }
            />
            <Form.Text>{t("OrgManagement.Users.Access.Username.Help")}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Password")}</Form.Label>
            <Form.Control
              type={createForm.showPassword ? "text" : "password"}
              value={createForm.password}
              autoComplete="new-password"
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  password: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.PasswordConfirm")}</Form.Label>
            <Form.Control
              type={createForm.showPassword ? "text" : "password"}
              value={createForm.passwordConfirm}
              autoComplete="new-password"
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  passwordConfirm: event.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Check
            className="mb-3"
            type="checkbox"
            label={t("OrgManagement.Users.Access.ShowPassword")}
            checked={createForm.showPassword}
            onChange={(event) =>
              setCreateForm((current) => ({
                ...current,
                showPassword: event.target.checked,
              }))
            }
          />
          <Form.Group className="mb-3">
            <Form.Label>{t("OrgManagement.Users.Access.Organization")}</Form.Label>
            <Form.Select
              value={createForm.company_id}
              onChange={(event) => {
                const company_id = event.target.value;
                setCreateForm((current) => ({
                  ...current,
                  company_id,
                  company_admin: company_id ? current.company_admin : false,
                }));
              }}
            >
              <option value="">{t("OrgManagement.Users.NoOrganization")}</option>
              {assignableOrgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            className="mb-3"
            type="checkbox"
            label={t("OrgManagement.Users.Access.CompanyAdmin")}
            checked={createForm.company_admin}
            disabled={!createForm.company_id}
            onChange={(event) =>
              setCreateForm((current) => ({
                ...current,
                company_admin: event.target.checked,
              }))
            }
          />
          <Form.Check
            type="checkbox"
            label={t("OrgManagement.Users.Create.Active")}
            checked={createForm.is_active}
            onChange={(event) =>
              setCreateForm((current) => ({
                ...current,
                is_active: event.target.checked,
              }))
            }
          />
          {createError ? <div className="text-danger mt-3">{createError}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
            {t("OrgManagement.Create.Cancel")}
          </Button>
          <Button variant="primary" disabled={creating} onClick={saveCreate}>
            {creating ? (
              <Spinner animation="border" size="sm" />
            ) : (
              t("OrgManagement.Users.Create.Submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
